import React, { useState } from 'react';
import { journeyFor, PROMOTE_PHASES } from '../../data/governanceJourney';
import { archetypeShortLabel } from '../../data/agentArchetypes';
import TrustWheel from './TrustWheel';

/**
 * One vertical sequence, from the moment they hit go.
 *
 * <h3>A spine, not a log</h3>
 *
 * Every step is a node on one line running down the page: a tick for what the
 * platform has done, a number for the step that needs the person. Beside the
 * marker, the step in its own words, a quieter line saying what that means, and
 * — where the step produced detail — that detail indented in mono beneath it,
 * so six drafted steps or five matched skills read as evidence rather than
 * prose.
 *
 * The step that needs them is a node on the same spine, carrying "Your part ·
 * n of m" and its input inline. That is the whole point: the platform's work
 * and the person's work are the same kind of thing, in the order they happen.
 * Put the questions on a separate screen and the two are back in different
 * places, which is what the three columns did.
 *
 * <h3>The record stops where the present begins</h3>
 *
 * Only steps that have happened are listed, and the active node is the last
 * thing drawn. Nothing below it exists, because nothing below it has happened.
 * A trail that draws its own future is a progress bar.
 *
 * @param {Array}  feed   completed steps `[{id, at, title, text, detail[], tone}]`
 * @param {Object} ask    the step needing them `{number, total, question, hint}`, or null
 * @param {Node}   children whatever answers the ask — rendered inside its node
 */
export default function BuildTrail({
  request, feed = [], ask = null, status, children,
  name, kind, stage, onExit, onReopen, canReopen,
}) {
  const done = ['completed', 'failed', 'cancelled', 'rejected'].includes(status);
  const stages = journeyFor(kind);
  const found = stages.findIndex((st) => st.id === stage);
  /* Before the platform has reported a stage there is still a journey, and it
     starts at the first one. Leaving this at -1 made every section "future",
     which reads as though nothing has begun. */
  const hereAt = found >= 0 ? found : 0;
  const knownStage = (id) => stages.some((st) => st.id === id);
  const atPromote = stages[hereAt]?.id === 'promote';
  /* Answered, not answering: the count is what they have already given, which
     is what "n of m from you" means on the approved screen. */
  /* Answered, not answering: what they have already given. */
  const given = ask ? Math.max(0, ask.number - 1) : 0;
  /* Per-stage open/closed. Undefined means "follow the journey" — the current
     stage open, the rest folded — so the trail behaves correctly before anyone
     has clicked anything, and remembers them once they have. */
  const [opened, setOpened] = useState({});

  /* Resolve each node's stage once, carrying the last seen one forward. */
  const stagedFeed = (() => {
    let carried = stages[0]?.id;
    return feed.map((e) => {
      if (knownStage(e.stage)) carried = e.stage;
      return { ...e, resolvedStage: carried };
    });
  })();

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '760px', margin: '0 auto' }}>
      {/* The header is what says this is the same screen, further down: what is
          being protected, what kind of thing it is, how far along, and a way
          out. Without it the spine reads as a page that opened on top. */}
      {name ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                      flexWrap: 'wrap', marginBottom: 'var(--p7-space-4)' }}>
          <h1 style={{ margin: 0, font: '700 22px/28px var(--p7-font-sans)',
                       letterSpacing: '-.02em' }}>{name}</h1>
          {kind ? <span className="p7-status">{archetypeShortLabel(kind)}</span> : null}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center',
                         gap: '9px' }}>
            {ask ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px',
                             font: 'var(--p7-text-caption)',
                             fontFamily: 'var(--p7-font-sans)',
                             color: 'var(--p7-ink-subtle)' }}>
                {/* No bar. A progress bar is a promise about the end expressed as
                    geometry — it cannot be drawn without declaring how long the
                    journey is, and that number moves whenever the journey gains
                    a step. What somebody needs is how much they have given so
                    far, which is true at every moment. */}
                {given} {given === 1 ? 'answer' : 'answers'} from you
              </span>
            ) : null}
            {onExit ? (
              <button type="button" className="p7-btn p7-btn--sm" onClick={onExit}>
                Save &amp; exit
              </button>
            ) : null}
          </span>
        </div>
      ) : null}

      {request ? (
        <div className="p7-card"
             style={{ padding: 'var(--p7-space-4)',
                      borderLeft: '3px solid var(--p7-brand, var(--p7-accent))',
                      marginBottom: 'var(--p7-space-4)' }}>
          <span className="p7-eyebrow" style={{ display: 'block', marginBottom: '3px' }}>
            Bringing under control
          </span>
          <p style={{ margin: 0, font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                      color: 'var(--p7-ink)', lineHeight: 1.55 }}>
            {request}
          </p>
        </div>
      ) : null}

      {/* ── The journey, one stage at a time ───────────────────────────────
          Seven sections, each the stage's own work. A completed stage folds to
          a single line, because once Identity is done the thing worth seeing is
          that it IS done, not the four lines proving it — they stay one click
          away. The stage holding the current question is open and cannot be
          folded shut: collapsing the thing being asked of you is never what you
          meant to do.

          This replaced one undifferentiated list of nodes. Identity and Intent
          HAD run in that list, but with nothing naming them, the journey read
          as a wall of sentences and those two stages looked like they had been
          skipped. */}
      <ul aria-label="Protection journey"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex',
                   flexDirection: 'column', gap: 'var(--p7-space-2)' }}>
        {stages.map((st, idx) => {
          /* An untagged node sits with the work it arrived beside, not under
             whatever stage happens to be current.
             Two rules, learned the hard way:
               - It must never VANISH. Filtering on an exact match alone dropped
                 untagged nodes off the trail entirely: the step happened, the
                 record said so, the screen showed nothing.
               - It must never JUMP. Sending it to the current stage filed
                 "Bound to your connection" and the design-time score under
                 Access, three headings away from the work they belong to.
             Inheriting the previous node's stage does both: narration is
             emitted in order, so its neighbour is the best evidence available,
             and anything before the first tagged node falls to stage one. */
          const mine = stagedFeed.filter((e) => e.resolvedStage === st.id);
          /* Same rule as the nodes, and for the same reason: a question whose
             stage we do not recognise is still a question being asked, and it
             belongs where the journey currently is. Matching exactly meant an
             ask without a stage rendered nowhere at all. */
          const asking = Boolean(ask) && (knownStage(ask.stage)
            ? ask.stage === st.id
            : hereAt === idx);
          const cleared = hereAt > idx || (hereAt === idx && !asking && mine.length > 0);
          const future = hereAt >= 0 && idx > hereAt;
          const open = asking || opened[st.id] === true
                       || (opened[st.id] === undefined && hereAt === idx);

          return (
            <li key={st.id}>
              <StageSection
                Icon={st.icon}
                name={st.name}
                definition={st.definition}
                count={mine.length}
                cleared={cleared}
                current={hereAt === idx}
                future={future}
                open={open}
                /* The stage being asked about stays open: folding away the
                   question you are being asked is never the intent. */
                lockOpen={asking}
                onToggle={() => setOpened((o) => ({ ...o, [st.id]: !open }))}
              >
                {st.id === 'promote' ? (
                  <p style={{ margin: '0 0 var(--p7-space-2)',
                              font: 'var(--p7-text-caption)',
                              fontFamily: 'var(--p7-font-sans)',
                              color: 'var(--p7-ink-subtle)' }}>
                    Promotion gate · {PROMOTE_PHASES.join(' · ')}
                  </p>
                ) : null}

                <ul aria-label={`${st.name} steps`}
                    style={{ listStyle: 'none', margin: 0, padding: 0, position: 'relative' }}>
                  {mine.map((e) => (
                    /* The step's own icon where the node IS one of the
                       console's steps; a tick where it is something the
                       platform did, which has no step and no icon there. */
                    <Node key={e.id} at={e.at} tone={e.tone}
                          Icon={e.icon}
                          marker="✓">
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px',
                                     flexWrap: 'wrap' }}>
                        <span style={TITLE}>{e.title || e.text}</span>
                        {/* Not offered once the agent exists: everything at or
                            before the commit is frozen, because answering the
                            commit step again registers a second agent. Whether
                            a step is still open is the sequence's answer, not
                            this view's — it asks rather than keeping its own
                            copy of where the commit is. */}
                        {e.stepKey && onReopen && (!canReopen || canReopen(e.stepKey)) ? (
                          <button type="button" className="p7-link"
                                  aria-label={`Change ${e.title || e.text}`}
                                  onClick={() => onReopen(e.stepKey)}
                                  style={{ border: 0, background: 'none', padding: 0,
                                           cursor: 'pointer', font: 'var(--p7-text-caption)',
                                           fontFamily: 'var(--p7-font-sans)',
                                           color: 'var(--p7-accent)' }}>
                            Change
                          </button>
                        ) : null}
                      </span>
                      {e.title && e.text ? <span style={MEANING}>{e.text}</span> : null}
                      {/* The score is the number this whole journey is for, so it
                          is drawn where it is earned rather than written into a
                          sentence that scrolls past. */}
                      {typeof e.score === 'number' ? (
                        <div style={{ marginTop: 'var(--p7-space-2)' }}>
                          <TrustWheel score={e.score} band={e.band}
                                      caption="Design-time score. It moves once it runs." />
                        </div>
                      ) : null}
                      {e.detail?.length ? (
                        <div data-testid="detail" style={DETAIL}>
                          {e.detail.map((d) => (
                            <div key={d} className="p7-mono" style={{ lineHeight: 1.7 }}>{d}</div>
                          ))}
                        </div>
                      ) : null}
                    </Node>
                  ))}

                  {asking ? (
          <Node active Icon={ask.icon}
                marker={String(ask.number)}>
            <span className="p7-mono" style={{ display: 'block', fontSize: '11px',
                                               letterSpacing: '.07em', textTransform: 'uppercase',
                                               color: 'var(--p7-accent)', fontWeight: 600 }}>
              Your part · question {ask.number}
            </span>
            <span style={{ ...TITLE, font: 'var(--p7-text-h3, 600 17px/24px inherit)',
                           fontFamily: 'var(--p7-font-sans)', fontWeight: 700, marginTop: '3px' }}>
              {ask.question}
            </span>
            {ask.hint ? <span style={MEANING}>{ask.hint}</span> : null}
            {children ? (
              <div className="p7-card" style={{ padding: 'var(--p7-space-4)',
                                                marginTop: 'var(--p7-space-3)' }}>
                {children}
              </div>
            ) : null}
          </Node>
                  ) : null}

                  {/* Under the current stage, so a waiting state cannot float
                      free of the work it is waiting on. Keyed on there being no
                      QUESTION at all — not on this stage being empty: the
                      platform is just as busy on a stage that has already
                      recorded steps, and hiding the line there made the trail
                      look stalled. */}
                  {!ask && !done && hereAt === idx ? (
                    <Node marker="" active>
                      <span style={MEANING}>
                        Working — nothing changes until you have seen what it found.
                      </span>
                    </Node>
                  ) : null}
                </ul>
              </StageSection>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * One stage of the journey: a header that is always readable, and a body that
 * folds.
 *
 * <h3>What the header has to say on its own</h3>
 *
 * A folded stage still has to answer "did this happen, and what was it".
 * So the header carries the stage's own icon — the same one the journey funnel
 * draws it with, owned by data/governanceJourney.js so the two screens cannot
 * disagree — its name, its state, and how many
 * steps are inside — enough that nobody has to open it to know where they are.
 * The stage's definition sits underneath while it is current or still to come,
 * and goes away once cleared, because by then it has been demonstrated rather
 * than described.
 */
function StageSection({
  Icon, name, definition, count, cleared, current, future,
  open, lockOpen, onToggle, children,
}) {
  const tone = cleared ? 'var(--p7-allow, #16a34a)'
    : current ? 'var(--p7-accent)' : 'var(--p7-ink-faint, #94a3b8)';

  return (
    /* Labelled and current-marked on the SECTION, so "where is this agent"
       is answerable from the accessibility tree rather than from styling. */
    <section aria-label={name}
             aria-current={current ? 'step' : undefined}
             style={{ border: '1px solid var(--p7-line-soft)',
                      borderRadius: 'var(--p7-radius, 8px)',
                      background: current ? 'var(--p7-surface)' : 'transparent',
                      opacity: future ? 0.6 : 1 }}>
      <button type="button"
              onClick={lockOpen ? undefined : onToggle}
              aria-expanded={open}
              aria-disabled={lockOpen ? true : undefined}
              style={{ width: '100%', display: 'flex', alignItems: 'center',
                       gap: 'var(--p7-space-3)', textAlign: 'left',
                       padding: 'var(--p7-space-3)', border: 0, background: 'none',
                       cursor: lockOpen ? 'default' : 'pointer' }}>
        <span aria-hidden="true"
              style={{ flex: 'none', width: '22px', height: '22px', borderRadius: '50%',
                       display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                       border: `1.5px solid ${tone}`, color: tone,
                       font: 'var(--p7-text-caption)', fontWeight: 700 }}>
          {Icon ? <Icon size={12} strokeWidth={2.25} aria-hidden="true" /> : null}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', font: 'var(--p7-text-ui)',
                         fontFamily: 'var(--p7-font-sans)', fontWeight: 700,
                         color: current ? 'var(--p7-ink)' : 'var(--p7-ink-subtle)' }}>
            {name}
            {count ? (
              <span style={{ marginLeft: '8px', fontWeight: 500,
                             font: 'var(--p7-text-caption)',
                             fontFamily: 'var(--p7-font-sans)',
                             color: 'var(--p7-ink-faint, #94a3b8)' }}>
                {count} step{count === 1 ? '' : 's'}
              </span>
            ) : null}
          </span>
          {!cleared && definition ? (
            <span style={{ display: 'block', marginTop: '1px',
                           font: 'var(--p7-text-caption)',
                           fontFamily: 'var(--p7-font-sans)',
                           color: 'var(--p7-ink-faint, #94a3b8)' }}>
              {definition}
            </span>
          ) : null}
        </span>
        {!lockOpen && (count > 0 || !future) ? (
          <span aria-hidden="true"
                style={{ flex: 'none', color: 'var(--p7-ink-faint, #94a3b8)',
                         transform: open ? 'rotate(90deg)' : 'none',
                         transition: 'transform .15s ease' }}>
            ›
          </span>
        ) : null}
      </button>
      {open ? (
        <div style={{ padding: '0 var(--p7-space-3) var(--p7-space-3)' }}>{children}</div>
      ) : null}
    </section>
  );
}

/** One step on the spine: the line, the marker, and what the step says. */
function Node({ at, marker, Icon, active = false, tone, children }) {
  return (
    <li style={{ display: 'flex', gap: '12px', alignItems: 'flex-start',
                 padding: '0 0 var(--p7-space-5)', position: 'relative' }}>
      <span className="p7-mono" style={{ flex: 'none', width: '34px', textAlign: 'right',
                                         color: 'var(--p7-ink-faint)', lineHeight: '24px' }}>
        {at || ''}
      </span>
      <span style={{ flex: 'none', position: 'relative', width: '24px' }}>
        {/* The spine itself, drawn per node so it stops at the last one. */}
        <span aria-hidden="true"
              style={{ position: 'absolute', left: '11px', top: '24px', bottom: '-20px',
                       width: '2px', background: 'var(--p7-line)' }} />
        <span
          data-testid="marker"
          style={{
            position: 'relative', display: 'grid', placeItems: 'center',
            width: '24px', height: '24px', borderRadius: '50%', fontSize: '11px',
            fontWeight: 700, zIndex: 1,
            border: `2px solid ${active ? 'var(--p7-accent)' : tone === 'hold'
              ? 'var(--p7-hold)' : 'var(--p7-allow)'}`,
            background: active ? 'var(--p7-accent)' : tone === 'hold'
              ? 'var(--p7-hold-bg)' : 'var(--p7-allow-bg)',
            color: active ? '#fff' : tone === 'hold'
              ? 'var(--p7-hold-ink)' : 'var(--p7-allow-ink)',
          }}
        >
          {Icon ? <Icon size={12} strokeWidth={2.25} aria-hidden="true" /> : marker}
        </span>
      </span>
      <span style={{ minWidth: 0, flex: 1, paddingTop: '1px' }}>{children}</span>
    </li>
  );
}

const TITLE = {
  display: 'block', font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
  fontWeight: 600, color: 'var(--p7-ink)',
};
const MEANING = {
  display: 'block', marginTop: '2px', font: 'var(--p7-text-caption)',
  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};
const DETAIL = {
  marginTop: '8px', paddingLeft: '12px', borderLeft: '2px solid var(--p7-line-soft)',
  fontSize: '11.5px', color: 'var(--p7-ink-subtle)',
};
